import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ESTIMATE_STATUS_LABELS,
  formatAddress,
  formatCents,
  formatDate,
  formatDateTime,
  formatPhone,
  formatWindow,
  JOB_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  relativeTime,
  type Invoice,
  type JobStatus,
  type LineItemInput,
  type Payment,
} from '@fsm/shared';
import { api } from '../lib/api';
import { DeleteRecord } from '../components/Selection';
import {
  invalidateJobWorld,
  keys,
  useEstimate,
  useInvoice,
  useJob,
  useTechs,
} from '../lib/queries';
import { useAuth } from '../lib/auth';
import { LineItemEditor } from '../components/LineItemEditor';
import { CommunicationLog } from '../components/CommunicationLog';
import { CatalogPicker } from '../components/CatalogPicker';
import { JobCostSheet } from '../components/JobCostSheet';
import { PaymentBadge } from './Invoices';
import { ContactCard } from '../components/ContactCard';
import { SignOffCard } from '../components/SignOffCard';
import { Icon } from '../components/icons';
import { PageFooter, useCrumb } from '../components/Layout';
import {
  ActivityFeed,
  FLOW_ICONS,
  FlowBar,
  flowIcon,
  RecordHeader,
  shortDate,
  shortTime,
  type FeedEntry,
  type FlowStep,
} from '../components/record';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Dot,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  StatusBadge,
  SummaryBand,
  Textarea,
  Toast,
  cx,
} from '../components/ui';

/**
 * The job page.
 *
 * Three columns, and which column a thing is in says what it is. The left rail
 * is context that does not change while you work the job — who, where, what
 * was said about it. The middle is the job itself, top to bottom in the order
 * the money moves: what happens next, what is being charged, what came in,
 * what was billed. The right is history, which you read and never edit.
 *
 * Nothing here computes a total or decides whether a status may change. The
 * flow bar asks the API which transitions are available and the invoice
 * reports its own balance; this file only draws them.
 */
export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { company, can } = useAuth();
  const { data: job, isLoading } = useJob(id);

  useCrumb(job ? `Job #${job.number}` : null);
  const { data: techs = [] } = useTechs();
  const { data: invoice } = useInvoice(job?.invoiceId);
  const { data: estimate } = useEstimate(job?.estimateId);

  /* Quotes a technician raised while working this job. */
  const quotedHere = job?.estimates ?? [];

  const [note, setNote] = useState('');
  const [editingItems, setEditingItems] = useState(false);
  const [draftItems, setDraftItems] = useState<LineItemInput[]>([]);
  const [toast, setToast] = useState<{ message: string; tone: 'error' | 'success' } | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const isOffice = can('admin', 'dispatcher');

  const fail = (e: unknown, fallback: string) =>
    setToast({ message: (e as { message?: string }).message ?? fallback, tone: 'error' });

  const setStatus = useMutation({
    mutationFn: (status: JobStatus) => api.jobs.setStatus(id!, status),
    onSuccess: () => invalidateJobWorld(qc, id),
    onError: (e) => fail(e, 'Could not update'),
  });

  const markEnRoute = useMutation({
    mutationFn: () => api.jobs.enRoute(id!),
    onSuccess: () => invalidateJobWorld(qc, id),
    onError: (e) => fail(e, 'Could not mark en route'),
  });

  const addNote = useMutation({
    mutationFn: () => api.jobs.addNote(id!, note.trim()),
    onSuccess: () => {
      setNote('');
      void qc.invalidateQueries({ queryKey: keys.job(id!) });
    },
    onError: (e) => fail(e, 'Could not add the note'),
  });

  const saveItems = useMutation({
    mutationFn: () => api.jobs.setLineItems(id!, draftItems),
    onSuccess: () => {
      setEditingItems(false);
      invalidateJobWorld(qc, id);
    },
    onError: (e) => fail(e, 'Could not save'),
  });

  /*
   * Reassigning from the job page.
   *
   * It goes through the same schedule endpoint the board's drag does, keeping
   * the existing window, so the double-booking check runs here too — a tech
   * swapped on this screen cannot end up with two jobs at two o'clock just
   * because the swap did not happen on the calendar.
   */
  const reassign = useMutation({
    mutationFn: (techId: string | null) =>
      api.jobs.schedule(id!, {
        assignedTechId: techId,
        scheduledStart: techId ? (job?.scheduledStart ?? null) : null,
        scheduledEnd: techId ? (job?.scheduledEnd ?? null) : null,
        allowConflict: false,
      }),
    onSuccess: () => invalidateJobWorld(qc, id),
    onError: (e) => fail(e, 'Could not reassign this job'),
  });

  const setLeadSource = useMutation({
    mutationFn: (leadSourceId: string | null) => api.jobs.update(id!, { leadSourceId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.job(id!) }),
    onError: (e) => fail(e, 'Could not set the lead source'),
  });

  /*
   * The office uploads too.
   *
   * The API always allowed it — the route is authenticated, not tech-only —
   * but until now there was no control for it, so a customer emailing a photo
   * of their meter meant asking a technician to put it on the job.
   */
  const uploadAttachment = useMutation({
    mutationFn: (file: File) => api.jobs.addAttachment(id!, file),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.job(id!) }),
    onError: (e) => fail(e, 'Could not upload that file'),
  });

  const setJobType = useMutation({
    mutationFn: (jobTypeId: string | null) => api.jobs.update(id!, { jobTypeId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.job(id!) }),
    onError: (e) => fail(e, 'Could not set the job type'),
  });

  const createInvoice = useMutation({
    mutationFn: () => api.invoices.createFromJob(id!, { dueInDays: 14 }),
    onSuccess: (created) => {
      invalidateJobWorld(qc, id);
      navigate(`/invoices/${created.id}`);
    },
    onError: (e) => fail(e, 'Could not create the invoice'),
  });

  if (isLoading || !job) {
    return (
      <div className="grid flex-1 place-items-center">
        <Spinner className="size-6 text-ink-4" />
      </div>
    );
  }

  const transitions = job.availableTransitions ?? [];

  const allows = (to: JobStatus) => transitions.some((t) => t.to === to && t.allowed);
  const locked = job.status === 'invoiced' || job.status === 'paid';

  /*
   * Schedule -> On my way -> Start -> Finish -> Invoice -> Pay.
   *
   * Six steps because that is the whole job, and a dispatcher glancing at the
   * page should be able to say where it has got to without reading a word.
   * Which of them can be pressed is not decided here: `allows` comes straight
   * from the transitions the API published with the job.
   */
  const settled = invoice?.totals?.isSettled ?? job.status === 'paid';
  // The most recent payment that still counts, for the line under the total.
  const lastPayment = (invoice?.payments ?? []).filter((p) => !p.voidedAt).at(-1) ?? null;

  const flowBusy = setStatus.isPending || markEnRoute.isPending || createInvoice.isPending;

  const flowSteps: FlowStep[] = [
    {
      key: 'schedule',
      label: 'Schedule',
      icon: flowIcon(FLOW_ICONS.schedule),
      done: Boolean(job.scheduledStart),
      sub: job.scheduledStart ? shortDate(job.scheduledStart) : null,
      onClick: isOffice ? () => setScheduling(true) : null,
    },
    {
      key: 'omw',
      label: 'On my way',
      icon: flowIcon(FLOW_ICONS.enRoute),
      done: Boolean(job.enRouteAt),
      sub: job.enRouteAt ? shortTime(job.enRouteAt) : null,
      onClick: !job.enRouteAt && job.scheduledStart ? () => markEnRoute.mutate() : null,
    },
    {
      key: 'start',
      label: 'Start',
      icon: flowIcon(FLOW_ICONS.start),
      done: Boolean(job.startedAt),
      sub: job.startedAt ? shortTime(job.startedAt) : null,
      onClick: allows('in_progress') ? () => setStatus.mutate('in_progress') : null,
    },
    {
      key: 'finish',
      label: 'Finish',
      icon: flowIcon(FLOW_ICONS.finish),
      done: Boolean(job.completedAt),
      sub: job.completedAt ? shortTime(job.completedAt) : null,
      onClick: allows('completed') ? () => setStatus.mutate('completed') : null,
    },
    {
      key: 'invoice',
      label: 'Invoice',
      icon: flowIcon(FLOW_ICONS.send),
      done: Boolean(job.invoiceId),
      sub: invoice ? `#${invoice.number}` : null,
      /*
       * Open to a technician as well as the office. A tech who has just
       * finished the work can raise the invoice and take payment on the spot,
       * which is the same thing the field app does — the server checks that
       * the job is theirs.
       */
      onClick: job.invoiceId
        ? () => navigate(`/invoices/${job.invoiceId}`)
        : job.status === 'completed'
          ? () => createInvoice.mutate()
          : null,
    },
    {
      key: 'pay',
      label: 'Pay',
      icon: flowIcon(FLOW_ICONS.pay),
      done: settled,
      sub: settled
        ? 'Paid'
        : invoice?.totals
          ? `${formatCents(invoice.totals.balanceDueCents)} due`
          : null,
      onClick: job.invoiceId ? () => navigate(`/invoices/${job.invoiceId}`) : null,
    },
  ];

  /*
   * Status changes and payments live in different tables and arrive on
   * different objects, but to whoever is reading the page they are one story.
   */
  const feedEntries: FeedEntry[] = [
    ...(job.statusEvents ?? []).map((e) => {
      const title = e.fromStatus
        ? `${JOB_STATUS_LABELS[e.fromStatus]} → ${JOB_STATUS_LABELS[e.toStatus]}`
        : 'Job created';
      return {
        id: `s:${e.id}`,
        at: e.createdAt,
        tone: 'status' as const,
        title,
        meta: [
          e.actorName ?? 'System',
          e.source === 'offline_sync'
            ? 'synced from the field'
            : e.source === 'mobile'
              ? 'from the phone'
              : null,
        ]
          .filter(Boolean)
          .join(' · '),
        // The server writes a note on some transitions that says exactly what
        // the title already says; printing it twice reads as a bug.
        detail: e.note && e.note !== title ? e.note : null,
      };
    }),
    ...(invoice?.payments ?? [])
      .filter((p) => !p.voidedAt)
      .map((p) => ({
        id: `p:${p.id}`,
        at: p.createdAt,
        tone: 'money' as const,
        title: `Payment of ${formatCents(p.amountCents)} (${PAYMENT_METHOD_LABELS[p.method].toLowerCase()})`,
        meta: p.receivedByName ?? 'Office',
        detail: p.reference,
      })),
  ];

  return (
    <>
      <RecordHeader
        eyebrow="Job details"
        number={`#${job.number}`}
        title={job.title}
        badges={
          <>
            <StatusBadge status={job.status} />
            {job.priority === 'emergency' && <Badge tone="red">Emergency</Badge>}
            {job.priority === 'high' && <Badge tone="amber">High priority</Badge>}
          </>
        }
        subtitle={
          <>
            {job.property && (
              <>
                <Icon name="pin" size={14} className="text-ink-4" />
                {formatAddress(job.property)}
                <Dot />
              </>
            )}
            Created {formatDate(job.createdAt)}
          </>
        }
        /*
         * The forward actions are all on the flow bar; what is up here is what
         * the job produced and the one way to call it off. A second row of
         * buttons that could also start the job is how two paths to one state
         * get out of step with each other.
         */
        actions={
          <>
            {invoice && (
              <Link to={`/invoices/${invoice.id}`}>
                <Button variant="primary" icon="cost">
                  View invoice
                  <Icon name="arrow" size={13} />
                </Button>
              </Link>
            )}
            {allows('cancelled') && (
              <Button variant="danger" onClick={() => setStatus.mutate('cancelled')}>
                Cancel job
              </Button>
            )}
          </>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        <div className="mx-auto grid max-w-[1860px] grid-cols-1 items-start gap-5 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[minmax(265px,0.9fr)_minmax(560px,2.6fr)_minmax(280px,1fr)]">
          {/* ---------------------------------------------------- left rail */}
          <div className="space-y-5">
            <ContactCard
              name={job.customer?.name ?? 'Customer'}
              role="Residential customer"
              phone={job.customer?.phone}
              email={job.customer?.email}
              property={job.property}
              customerId={job.customerId}
              jobId={job.id}
              footLink={{ to: `/customers/${job.customerId}`, label: 'View customer profile' }}
            />

            <Card
              title="Schedule"
              bodyClassName="px-[21px] pt-1 pb-[21px]"
              action={
                isOffice && (
                  <button
                    type="button"
                    onClick={() => setScheduling(true)}
                    className="text-[13px] font-semibold text-accent hover:underline"
                  >
                    {job.scheduledStart ? 'Reschedule' : 'Schedule'}
                  </button>
                )
              }
            >
              {job.scheduledStart && job.scheduledEnd ? (
                <div className="flex items-center gap-3">
                  {/*
                   * A torn-off calendar page. The day of the month is the one
                   * thing anyone reads off a schedule at a glance, and it was
                   * previously the fourth word of a sentence.
                   */}
                  <div className="w-[41px] shrink-0 overflow-hidden rounded-[7px] border border-line-2 text-center">
                    <div className="bg-raised py-0.5 text-[10px] font-semibold tracking-[0.1em] text-ink-3 uppercase">
                      {new Intl.DateTimeFormat('en-US', { month: 'short' }).format(
                        new Date(job.scheduledStart),
                      )}
                    </div>
                    <div className="py-1 font-display text-[17px] font-bold tnum">
                      {new Date(job.scheduledStart).getDate()}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[14px] font-semibold">
                      {new Intl.DateTimeFormat('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                      }).format(new Date(job.scheduledStart))}
                    </div>
                    <div className="mt-1 tnum text-[13px] text-ink-3">
                      {formatWindow(job.scheduledStart, job.scheduledEnd)}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-[13px] text-ink-3">Not scheduled — sitting in the backlog.</p>
              )}

              <div className="mt-3 border-t border-line pt-3">
                <div className="flex items-center gap-2.5">
                  {job.assignedTech ? (
                    <>
                      <Avatar
                        name={job.assignedTech.name}
                        color={job.assignedTech.colorHex}
                        size={32}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-[13.5px] font-medium">
                          {job.assignedTech.name}
                        </div>
                        <div className="text-[12px] text-ink-4">
                          {job.assignedTech.phone
                            ? formatPhone(job.assignedTech.phone)
                            : 'Technician'}
                        </div>
                      </div>
                    </>
                  ) : (
                    <span className="text-[13px] text-ink-3">Unassigned</span>
                  )}
                </div>

                {isOffice && (
                  <Select
                    className="mt-2.5"
                    value={job.assignedTechId ?? ''}
                    disabled={reassign.isPending}
                    onChange={(e) => reassign.mutate(e.target.value || null)}
                  >
                    <option value="">Unassigned — back to the backlog</option>
                    {techs.map((tech) => (
                      <option key={tech.id} value={tech.id}>
                        {tech.name}
                      </option>
                    ))}
                  </Select>
                )}
              </div>

              {job.publicToken && (
                <a
                  href={`/api/status/${job.publicToken}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 flex items-center gap-2 text-[13px] font-semibold text-accent hover:underline"
                >
                  Customer status page
                  <Icon name="arrow" size={13} />
                </a>
              )}
            </Card>

            <Card
              title="Classification"
              action={<Icon name="grid" size={17} className="text-ink-4" />}
              bodyClassName="px-[21px] pt-1 pb-[21px]"
            >
              <div className="space-y-4">
                <div>
                  <div className="mb-2 text-[14px] text-ink-3">Job type</div>
                  {isOffice ? (
                    <CatalogPicker
                      kind="job_type"
                      value={job.jobTypeId}
                      placeholder="Drain clearing, Install, Warranty…"
                      onChange={(jobTypeId) => setJobType.mutate(jobTypeId)}
                    />
                  ) : (
                    <p className="text-[13px]">{job.jobTypeName ?? 'Not set'}</p>
                  )}
                </div>

                <div>
                  <div className="mb-2 text-[14px] text-ink-3">Lead source</div>
                  {isOffice ? (
                    <CatalogPicker
                      kind="lead_source"
                      value={job.leadSourceId}
                      placeholder="Van sign, Nextdoor, Referral…"
                      onChange={(leadSourceId) => setLeadSource.mutate(leadSourceId)}
                    />
                  ) : (
                    <p className="text-[13px]">{job.leadSourceName ?? 'Not set'}</p>
                  )}
                </div>
              </div>
            </Card>

            <Card title="Notes" action={<Icon name="chat" size={17} className="text-ink-4" />}>
              <div className="flex gap-2">
                <Textarea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add an internal note…"
                  className="flex-1"
                />
                <Button
                  variant="primary"
                  disabled={!note.trim()}
                  loading={addNote.isPending}
                  onClick={() => addNote.mutate()}
                >
                  Add
                </Button>
              </div>

              <ul className="mt-4 space-y-3">
                {(job.notes ?? []).map((n) => (
                  <li key={n.id} className="border-b border-line pb-3 last:border-b-0 last:pb-0">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[11.5px] text-ink-4">
                      <span className="font-medium text-ink-2">{n.authorName ?? 'System'}</span>
                      <span>{relativeTime(n.createdAt)}</span>
                      {n.visibility === 'customer' && <Badge tone="blue">Customer visible</Badge>}
                    </div>
                    <p className="mt-1 text-[13px] leading-relaxed whitespace-pre-wrap">{n.body}</p>
                  </li>
                ))}
                {(job.notes ?? []).length === 0 && (
                  <li className="py-3 text-center text-[12.5px] text-ink-4">No notes yet.</li>
                )}
              </ul>
            </Card>

            <Card
              title="Attachments"
              action={
                isOffice && (
                  <>
                    <input
                      ref={fileInput}
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadAttachment.mutate(file);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      size="sm"
                      loading={uploadAttachment.isPending}
                      onClick={() => fileInput.current?.click()}
                    >
                      Upload
                    </Button>
                  </>
                )
              }
              bodyClassName={job.attachments?.length ? 'p-3' : 'p-0'}
            >
              {job.attachments && job.attachments.length > 0 ? (
                <div className="grid grid-cols-2 gap-2.5">
                  {job.attachments.map((a) => (
                    <a
                      key={a.id}
                      href={a.url}
                      target="_blank"
                      rel="noreferrer"
                      className="group block overflow-hidden rounded-lg border border-line"
                    >
                      <img
                        src={a.url}
                        alt={a.caption ?? a.filename}
                        className={cx(
                          'aspect-4/3 w-full object-cover transition-transform group-hover:scale-[1.03]',
                          a.kind === 'signature' && 'bg-white object-contain p-2',
                        )}
                      />
                      <div className="truncate px-2 py-1.5 text-[11px] text-ink-3">
                        {a.kind === 'signature' ? 'Signature' : a.caption || a.filename}
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="px-4 py-6 text-center text-[12.5px] text-ink-4">
                  Nothing here yet. Techs add photos from the phone, and the office can upload anything a customer sends in.
                </p>
              )}
            </Card>

            <SignOffCard job={job} />
          </div>

          {/* ------------------------------------------------------- middle */}
          <div className="space-y-5">
            <FlowBar
              steps={flowSteps}
              busy={flowBusy}
              title="Job progress"
              status={
                <>
                  <span
                    className={cx(
                      'size-1.5 rounded-full',
                      job.status === 'cancelled' ? 'bg-danger' : 'bg-good',
                    )}
                    aria-hidden
                  />
                  {JOB_STATUS_LABELS[job.status]}
                </>
              }
            />

            <Card
              title="Line items"
              icon="file"
              count={job.lineItems?.length ?? 0}
              divided
              bodyClassName="p-0"
              band={
                /*
                 * The band says where the money stands, not what was charged:
                 * settled, part paid, or still only a figure on a job that has
                 * not been invoiced. The table above it answers "for what".
                 */
                (job.totals?.totalCents ?? 0) > 0 ? (
                  <SummaryBand
                    tone={invoice?.totals?.isSettled ? 'good' : 'good'}
                    icon={invoice?.totals?.isSettled ? 'check' : undefined}
                    label={
                      invoice?.totals?.isSettled
                        ? 'Paid in full'
                        : invoice
                          ? `Invoiced — ${formatCents(invoice.totals?.balanceDueCents ?? 0)} outstanding`
                          : 'Not invoiced yet'
                    }
                    caption={
                      invoice?.totals?.isSettled && lastPayment
                        ? `Payment received by ${PAYMENT_METHOD_LABELS[lastPayment.method].toLowerCase()}${lastPayment.reference ? ` · ${lastPayment.reference}` : ''}`
                        : undefined
                    }
                    amountLabel="Total"
                    amount={formatCents(job.totals?.totalCents ?? 0)}
                  />
                ) : undefined
              }
              action={
                isOffice &&
                !locked &&
                (editingItems ? (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setEditingItems(false)}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      loading={saveItems.isPending}
                      onClick={() => saveItems.mutate()}
                    >
                      Save
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => {
                      setDraftItems(
                        (job.lineItems ?? []).map((li) => ({
                          priceBookItemId: li.priceBookItemId,
                          name: li.name,
                          description: li.description,
                          kind: li.kind,
                          quantity: li.quantity,
                          unitPriceCents: li.unitPriceCents,
                          taxable: li.taxable,
                          position: li.position,
                        })),
                      );
                      setEditingItems(true);
                    }}
                  >
                    Edit
                  </Button>
                ))
              }
            >
              <LineItemEditor
                value={
                  editingItems
                    ? draftItems
                    : (job.lineItems ?? []).map((li) => ({
                        priceBookItemId: li.priceBookItemId,
                        name: li.name,
                        description: li.description,
                        kind: li.kind,
                        quantity: li.quantity,
                        unitPriceCents: li.unitPriceCents,
                        taxable: li.taxable,
                        position: li.position,
                      }))
                }
                onChange={setDraftItems}
                taxRateBps={company?.taxRateBps ?? 0}
                readOnly={!editingItems}
                hideTotal={(job.totals?.totalCents ?? 0) > 0}
              />
            </Card>

            {isOffice && (
              <JobCostSheet
                jobId={job.id}
                items={job.costItems ?? []}
                internalDiscountCents={job.internalDiscountCents}
                billedTotalCents={job.totals?.totalCents ?? null}
                currency={company?.currency}
                onChanged={() => void qc.invalidateQueries({ queryKey: keys.job(id!) })}
                onError={(message) => setToast({ message, tone: 'error' })}
              />
            )}

            <PaymentHistory invoice={invoice} />

            <Card
              title="Invoices"
              action={
                isOffice &&
                !job.invoiceId &&
                job.status === 'completed' && (
                  <Button
                    size="sm"
                    variant="primary"
                    loading={createInvoice.isPending}
                    onClick={() => createInvoice.mutate()}
                  >
                    Create invoice
                  </Button>
                )
              }
              bodyClassName="p-0"
            >
              {invoice ? (
                <Link
                  to={`/invoices/${invoice.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-raised"
                >
                  <span className="tnum text-[13px] font-semibold">#{invoice.number}</span>
                  <PaymentBadge invoice={invoice} />
                  <span className="ml-auto tnum text-[14px] font-semibold">
                    {formatCents(invoice.totals?.totalCents ?? 0)}
                  </span>
                  {invoice.totals && invoice.totals.balanceDueCents > 0 && (
                    <span className="tnum text-[12px] text-warn">
                      {formatCents(invoice.totals.balanceDueCents)} due
                    </span>
                  )}
                </Link>
              ) : (
                <p className="px-4 py-5 text-center text-[12.5px] text-ink-4">
                  {job.status === 'completed'
                    ? 'Finished and not billed yet.'
                    : 'Nothing billed yet — an invoice is raised once the job is finished.'}
                </p>
              )}
            </Card>

            {/*
              Two different relationships, deliberately labelled apart.

              The estimate a job CAME FROM is its origin — one, fixed, already
              won. Estimates QUOTED HERE are the opposite end: work a
              technician was asked about while on site, not yet sold, and
              there can be several. Merging them into one list would put a
              closed sale and an open opportunity on the same footing, which
              is the distinction the office actually works from.
            */}
            <Card title="Estimates" bodyClassName="p-0">
              {estimate && (
                <Link
                  to={`/estimates/${estimate.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-raised"
                >
                  <span className="tnum text-[13px] font-semibold">#{estimate.number}</span>
                  <Badge tone={estimate.status === 'approved' ? 'emerald' : 'slate'}>
                    {ESTIMATE_STATUS_LABELS[estimate.status]}
                  </Badge>
                  <span className="ml-auto truncate text-[13px] text-ink-2">{estimate.title}</span>
                  <span className="shrink-0 text-[12px] text-ink-4">came from</span>
                </Link>
              )}

              {quotedHere.map((quote) => (
                <Link
                  key={quote.id}
                  to={`/estimates/${quote.id}`}
                  className="flex items-center gap-3 border-t border-line px-4 py-3 first:border-t-0 hover:bg-raised"
                >
                  <span className="tnum text-[13px] font-semibold">#{quote.number}</span>
                  <Badge tone={quote.status === 'approved' ? 'emerald' : 'slate'}>
                    {ESTIMATE_STATUS_LABELS[quote.status]}
                  </Badge>
                  <span className="truncate text-[13px] text-ink-2">{quote.title}</span>
                  <span className="tnum ml-auto shrink-0 text-[13px] font-semibold">
                    {formatCents(quote.totalCents)}
                  </span>
                  <span className="shrink-0 text-[12px] text-ink-4">quoted here</span>
                </Link>
              ))}

              {!estimate && quotedHere.length === 0 && (
                <p className="px-4 py-5 text-center text-[12.5px] text-ink-4">
                  This job did not come from an estimate, and nothing has been quoted here.
                </p>
              )}
            </Card>
          </div>

          {/* --------------------------------------------------- right rail */}
          <div className="space-y-5">
            <ActivityFeed entries={feedEntries} />
            {isOffice && <CommunicationLog jobId={job.id} title="Calls & texts" />}
          </div>
        </div>

        {can('admin') && (
          <DeleteRecord
            one="job"
            many="jobs"
            warning="Line items, photos, notes and status history go with it. A job that has been invoiced cannot be deleted — cancel it instead."
            onConfirm={() => api.jobs.bulkRemove([job.id])}
            onDeleted={() => {
              void qc.invalidateQueries({ queryKey: ['jobs'] });
              navigate('/jobs');
            }}
          />
        )}

        <PageFooter label={`Job #${job.number}`} />
      </div>

      <ScheduleModal
        open={scheduling}
        onClose={() => setScheduling(false)}
        job={job}
        techs={techs}
        onDone={() => {
          setScheduling(false);
          invalidateJobWorld(qc, id);
        }}
        onError={(m) => setToast({ message: m, tone: 'error' })}
      />

      {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
    </>
  );
}

// --- money in ---------------------------------------------------------------

function PaymentHistory({ invoice }: { invoice: Invoice | undefined }) {
  const payments = (invoice?.payments ?? []).filter((p) => !p.voidedAt);

  return (
    <Card title="Payment history" bodyClassName="p-0">
      {payments.length === 0 ? (
        <p className="px-4 py-5 text-center text-[12.5px] text-ink-4">
          {invoice ? 'Nothing received against this invoice yet.' : 'No payments on this job.'}
        </p>
      ) : (
        <>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-line bg-raised text-[12px] font-medium text-ink-3">
                <th className="px-4 py-2 text-left font-semibold">Date</th>
                <th className="px-4 py-2 text-left font-semibold">Method</th>
                <th className="px-4 py-2 text-left font-semibold">Reference</th>
                <th className="px-4 py-2 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p: Payment) => (
                <tr key={p.id} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-2.5 tnum whitespace-nowrap">
                    {formatDateTime(p.createdAt)}
                  </td>
                  <td className="px-4 py-2.5">{PAYMENT_METHOD_LABELS[p.method]}</td>
                  <td className="px-4 py-2.5 text-ink-3">{p.reference ?? '—'}</td>
                  <td className="px-4 py-2.5 text-right tnum font-semibold text-good">
                    {formatCents(p.amountCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {invoice?.totals && (
            <div className="flex items-baseline justify-between border-t border-line bg-raised px-4 py-2.5 text-[13px]">
              <span className="text-ink-3">Balance due</span>
              <span
                className={cx(
                  'tnum text-[15px] font-semibold',
                  invoice.totals.balanceDueCents > 0 ? 'text-warn' : 'text-good',
                )}
              >
                {formatCents(invoice.totals.balanceDueCents)}
              </span>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// --- scheduling -------------------------------------------------------------

function ScheduleModal({
  open,
  onClose,
  job,
  techs,
  onDone,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  job: {
    id: string;
    assignedTechId: string | null;
    scheduledStart: string | null;
    scheduledEnd: string | null;
    version: number;
  };
  techs: Array<{ id: string; name: string }>;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const initial = job.scheduledStart ? new Date(job.scheduledStart) : new Date();
  const [techId, setTechId] = useState(job.assignedTechId ?? '');
  const [day, setDay] = useState(
    `${initial.getFullYear()}-${String(initial.getMonth() + 1).padStart(2, '0')}-${String(initial.getDate()).padStart(2, '0')}`,
  );
  const [time, setTime] = useState(
    `${String(initial.getHours()).padStart(2, '0')}:${String(initial.getMinutes()).padStart(2, '0')}`,
  );
  const [mins, setMins] = useState(
    job.scheduledStart && job.scheduledEnd
      ? Math.round(
          (new Date(job.scheduledEnd).getTime() - new Date(job.scheduledStart).getTime()) / 60_000,
        )
      : 120,
  );
  const [conflictDetails, setConflictDetails] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (allowConflict: boolean) => {
      const start = new Date(`${day}T00:00:00`);
      const [hh, mm] = time.split(':').map(Number);
      start.setHours(hh ?? 9, mm ?? 0, 0, 0);
      return api.jobs.schedule(job.id, {
        assignedTechId: techId || null,
        scheduledStart: techId ? start.toISOString() : null,
        scheduledEnd: techId ? new Date(start.getTime() + mins * 60_000).toISOString() : null,
        allowConflict,
      });
    },
    onSuccess: onDone,
    onError: (e) => {
      const err = e as { status?: number; message?: string; details?: { conflicts?: unknown[] } };
      if (err.status === 409 && err.details?.conflicts) {
        setConflictDetails(err.message ?? 'That double-books this technician');
      } else {
        onError(err.message ?? 'Could not save the schedule');
      }
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Schedule job"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {conflictDetails && (
            <Button variant="danger" onClick={() => save.mutate(true)}>
              Double-book anyway
            </Button>
          )}
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(false)}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Technician" hint="clear to return to the backlog">
          <Select value={techId} onChange={(e) => setTechId(e.target.value)}>
            <option value="">Backlog (unassigned)</option>
            {techs.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Day">
            <Input type="date" value={day} disabled={!techId} onChange={(e) => setDay(e.target.value)} />
          </Field>
          <Field label="Start">
            <Input type="time" value={time} disabled={!techId} onChange={(e) => setTime(e.target.value)} />
          </Field>
          <Field label="Length">
            <Select value={mins} disabled={!techId} onChange={(e) => setMins(Number(e.target.value))}>
              {[30, 60, 90, 120, 180, 240, 360, 480].map((m) => (
                <option key={m} value={m}>
                  {m < 60 ? `${m} min` : `${m / 60} hr`}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {conflictDetails && (
          <p className="rounded-lg bg-warn-soft px-3 py-2 text-[13px] text-warn">
            {conflictDetails} You can double-book deliberately if that is what you intend.
          </p>
        )}
      </div>
    </Modal>
  );
}
